import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  createPayrollItemApi,
  DpPayrollItem,
  DpPayrollItemKind,
  listPayrollItemsApi,
  PAYROLL_ITEM_KIND_LABELS,
  updatePayrollItemApi,
} from "../api/payrollApi";

const emptyForm = {
  code: "",
  name: "",
  kind: "PROVENTO" as DpPayrollItemKind,
  inInss: true,
  inIrrf: true,
  inFgts: true,
  externalCode: "",
  sortOrder: "100",
  isActive: true,
};

const incidence = (item: DpPayrollItem) =>
  [item.inInss && "INSS", item.inIrrf && "IR", item.inFgts && "FGTS"].filter(Boolean).join(" · ") || "—";

/** Aba "Rubricas" em Cadastros: as verbas da folha e o código de cada uma no sistema do contador. */
export default function PayrollItemsSettings({ token }: { token: string }) {
  const [items, setItems] = useState<DpPayrollItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ item?: DpPayrollItem } | null>(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listPayrollItemsApi(token));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as rubricas.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (item?: DpPayrollItem) => {
    setForm(
      item
        ? {
            code: item.code,
            name: item.name,
            kind: item.kind,
            inInss: item.inInss,
            inIrrf: item.inIrrf,
            inFgts: item.inFgts,
            externalCode: item.externalCode ?? "",
            sortOrder: String(item.sortOrder),
            isActive: item.isActive,
          }
        : emptyForm
    );
    setModal({ item });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (form.name.trim().length < 2) return toast.error("Informe o nome da rubrica.");
    const system = modal.item?.isSystem;
    setSaving(true);
    try {
      if (modal.item) {
        await updatePayrollItemApi(
          token,
          modal.item.id,
          system
            ? { name: form.name.trim(), externalCode: form.externalCode.trim() || null, sortOrder: Number(form.sortOrder) }
            : {
                name: form.name.trim(),
                kind: form.kind,
                inInss: form.inInss,
                inIrrf: form.inIrrf,
                inFgts: form.inFgts,
                externalCode: form.externalCode.trim() || null,
                sortOrder: Number(form.sortOrder),
                isActive: form.isActive,
              }
        );
      } else {
        await createPayrollItemApi(token, {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          kind: form.kind,
          inInss: form.inInss,
          inIrrf: form.inIrrf,
          inFgts: form.inFgts,
          externalCode: form.externalCode.trim() || null,
          sortOrder: Number(form.sortOrder),
        });
      }
      toast.success("Rubrica salva.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a rubrica.");
    } finally {
      setSaving(false);
    }
  };

  const system = modal?.item?.isSystem ?? false;

  return (
    <>
      <div className="dpControls">
        <button className="dpBtn dpBtn--primary" onClick={() => openModal()}>
          Nova rubrica
        </button>
      </div>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        As rubricas do sistema seguem a lei: dá para mudar só o nome e o código externo — o código da verba no sistema
        do contador, que vai no CSV para casar com a folha dele. Crie rubricas próprias para prêmios, gratificações,
        plano de saúde e outros descontos.
      </p>

      {loading ? (
        <div className="dpEmpty">Carregando...</div>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Rubrica</th>
                <th>Natureza</th>
                <th>Entra na base de</th>
                <th>Código externo</th>
                <th>Origem</th>
                <th style={{ width: 90 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={item.isActive ? undefined : { opacity: 0.55 }}>
                  <td>
                    <strong>{item.name}</strong>
                    <div className="dpFormHint">{item.code}</div>
                  </td>
                  <td>{PAYROLL_ITEM_KIND_LABELS[item.kind]}</td>
                  <td>{incidence(item)}</td>
                  <td>{item.externalCode || "—"}</td>
                  <td>
                    <span className={`dpBadge ${item.isSystem ? "dpBadge--gray" : "dpBadge--blue"}`}>
                      {item.isSystem ? (item.isManual ? "Sistema · lançável" : "Sistema · calculada") : "Do DP"}
                    </span>
                    {!item.isActive ? <div className="dpFormHint">Inativa</div> : null}
                  </td>
                  <td>
                    <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openModal(item)}>
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.item ? `Editar rubrica ${modal.item.code}` : "Nova rubrica"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                {system ? (
                  <p className="dpNotice" style={{ margin: 0 }}>
                    Rubrica do sistema: natureza e incidências vêm da lei e não mudam.
                  </p>
                ) : null}
                {!modal.item ? (
                  <div className="dpFormGroup">
                    <label htmlFor="itemCode">Código *</label>
                    <input
                      id="itemCode"
                      className="dpInput"
                      maxLength={40}
                      placeholder="PREMIO_META"
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Maiúsculas, números e _. Não muda depois.</span>
                  </div>
                ) : null}
                <div className="dpFormGroup">
                  <label htmlFor="itemName">Nome *</label>
                  <input
                    id="itemName"
                    className="dpInput"
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    disabled={saving}
                  />
                </div>
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="itemKind">Natureza</label>
                    <select
                      id="itemKind"
                      className="dpSelect"
                      value={form.kind}
                      onChange={(e) => setForm({ ...form, kind: e.target.value as DpPayrollItemKind })}
                      disabled={saving || system}
                    >
                      {system && form.kind === "INFORMATIVO" ? <option value="INFORMATIVO">Informativo</option> : null}
                      <option value="PROVENTO">Provento</option>
                      <option value="DESCONTO">Desconto</option>
                    </select>
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="itemExternal">Código externo</label>
                    <input
                      id="itemExternal"
                      className="dpInput"
                      maxLength={40}
                      placeholder="Ex.: 0150"
                      value={form.externalCode}
                      onChange={(e) => setForm({ ...form, externalCode: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                </div>
                <fieldset className="dpBenefitBlock" disabled={saving || system}>
                  <strong>Entra na base de</strong>
                  {(
                    [
                      ["inInss", "INSS"],
                      ["inIrrf", "Imposto de renda"],
                      ["inFgts", "FGTS"],
                    ] as Array<["inInss" | "inIrrf" | "inFgts", string]>
                  ).map(([key, label]) => (
                    <label key={key} className="dpCheckboxRow">
                      <input type="checkbox" checked={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} />{" "}
                      {label}
                    </label>
                  ))}
                  <span className="dpFormHint">
                    Prêmio e gratificação costumam entrar nas três; plano de saúde e outros descontos, em nenhuma.
                  </span>
                </fieldset>
                {modal.item && !system ? (
                  <label className="dpCheckboxRow">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      disabled={saving}
                    />{" "}
                    Ativa (inativa não aparece para novos lançamentos)
                  </label>
                ) : null}
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
